// Boomin' — Coupon Detail screen (/c/:id)

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory, useParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  BRAND,
  BRAND_SOFT,
  ACCENT,
  PictX,
  PictName,
  AppHeader,
  AppFooter,
  useBoominFx,
} from "./shared";
import { GOOGLE_MAPS_API_KEY } from "../../Constants";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
  RedesignCategory,
  addFavorite,
  categoryLabel,
  couponTag,
  fetchCoupon,
  formatExpiry,
  inferCategory,
  isCouponFavorited,
  isUserLoggedIn,
  removeFavorite,
} from "./api";
import { Coupon, Hub } from "../../Types/App";

const brand = BRAND;
const brandSoft = BRAND_SOFT;
const accent = ACCENT;

const fullAddress = (h: Hub): string => {
  const parts = [h.state, h.city, h.address_line_1, h.address_line_2].filter(Boolean);
  return parts.join("");
};

export const DetailScreen: React.FC = () => {
  useBoominFx();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [hub, setHub] = useState<Hub | null>(null);
  const [available, setAvailable] = useState<boolean>(true);
  const [favorited, setFavorited] = useState<boolean>(false);
  const [favBusy, setFavBusy] = useState<boolean>(false);
  const sub = "#777";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCoupon(id);
      setCoupon(res.coupon);
      setHub(res.hub);
      setAvailable(res.available);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "クーポンが見つかりませんでした");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Reflect the current favorite state once we know which coupon we're on.
  useEffect(() => {
    let active = true;
    isCouponFavorited(id)
      .then((v) => active && setFavorited(v))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id]);

  const onToggleFavorite = async () => {
    if (favBusy) return;
    if (!isUserLoggedIn()) {
      // Same OAuth round-trip the "use" CTA uses, returning to this page.
      try {
        sessionStorage.setItem("boomin_return_to", `/c/${id}`);
      } catch {
        /* ignore */
      }
      window.location.href = "/public/oauth/instabase";
      return;
    }
    const next = !favorited;
    setFavorited(next); // optimistic
    setFavBusy(true);
    try {
      if (next) {
        await addFavorite(id);
      } else {
        await removeFavorite(id);
      }
    } catch (e: any) {
      setFavorited(!next); // revert on failure
      alert(typeof e === "string" ? e : e?.message || "お気に入りの更新に失敗しました");
    } finally {
      setFavBusy(false);
    }
  };

  const cat: RedesignCategory = coupon ? inferCategory(coupon, hub) : "shop";
  const heroImg = coupon?.image || hub?.image || "";
  const expiry = formatExpiry(coupon?.period_end);
  const tag = couponTag(coupon?.period_end);
  const catLabel = categoryLabel(cat);

  const breadcrumb = coupon && hub
    ? buildBreadcrumb({
        state: hub.state,
        city: hub.city,
        addressLine1: hub.address_line_1,
        cat,
        catLabel,
        hubName: hub.name,
      })
    : null;

  const onUse = () => {
    if (!coupon) return;
    if (!available) return; // already used
    if (!isUserLoggedIn()) {
      // Send the user through the legacy OAuth flow and bring them back here.
      try {
        sessionStorage.setItem("boomin_return_to", `/c/${coupon.id}`);
      } catch {
        /* ignore */
      }
      window.location.href = "/public/oauth/instabase";
      return;
    }
    history.push(`/active/${coupon.id}`);
  };

  return (
    <>
      <Helmet>
        {coupon && (
          <title>
            {coupon.title}
            {hub?.name ? `｜${hub.name}` : ""}
            ｜ローカルクーポンのBoomin'（ブーミン）
          </title>
        )}
        {coupon && (
          <meta
            name="description"
            content={(coupon.description || "").slice(0, 140)}
          />
        )}
        {breadcrumb && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumb.jsonLd)}
          </script>
        )}
      </Helmet>
      <AppHeader />
      <div
        style={{
          background: "#FFFBF7",
          minHeight: "100vh",
          paddingBottom: 120,
          position: "relative",
        }}
      >
        {breadcrumb && <Breadcrumb crumbs={breadcrumb.visible} />}
        {/* Hero */}
        <div style={{ position: "relative", height: 280, background: brandSoft }}>
          {heroImg ? (
            <img
              src={heroImg}
              alt={hub?.name || ""}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PictX name="ticket" size={96} stroke={brand} strokeWidth={1.2} />
            </div>
          )}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.22) 0%, transparent 35%, transparent 70%, rgba(0,0,0,0.15) 100%)",
            }}
          />
          {/* Share / favorite actions live in the page body (overlaid on the
              hero), not in the global header. */}
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <button
              aria-label={favorited ? "お気に入りから削除" : "お気に入りに追加"}
              aria-pressed={favorited}
              style={{
                ...shareBtn,
                borderColor: favorited ? brand : "rgba(255,255,255,0.7)",
                background: favorited ? brandSoft : "rgba(255,255,255,0.92)",
                cursor: favBusy ? "default" : "pointer",
              }}
              onClick={onToggleFavorite}
              disabled={favBusy}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={favorited ? brand : "none"}
                stroke={favorited ? brand : "#1A1A1A"}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
              </svg>
            </button>
            <button
              aria-label="シェア"
              style={{
                ...shareBtn,
                borderColor: "rgba(255,255,255,0.7)",
                background: "rgba(255,255,255,0.92)",
              }}
              onClick={() => {
                const url = window.location.href;
                if (navigator.share) {
                  navigator
                    .share({ title: coupon?.title || "Boomin'", url })
                    .catch(() => {});
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(url).catch(() => {});
                }
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1A1A1A"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="6" cy="12" r="2.5" />
                <circle cx="18" cy="6" r="2.5" />
                <circle cx="18" cy="18" r="2.5" />
                <path d="M8 11l8-4M8 13l8 4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Ticket */}
        <div style={{ padding: "0 16px", marginTop: -50, position: "relative", zIndex: 4 }}>
          {coupon ? (
            <Ticket coupon={coupon} hub={hub} expiry={expiry} tag={tag} />
          ) : (
            <TicketSkeleton />
          )}
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              margin: "24px 20px 0",
              padding: "16px 18px",
              background: "#fff",
              border: "1px solid #FFDAD0",
              borderRadius: 16,
              color: "#A6321E",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {error}
          </div>
        )}

        {/* Shop info */}
        {hub && coupon && (
          <div style={{ padding: "24px 20px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                color: sub,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  background: brandSoft,
                  color: brand,
                  fontWeight: 700,
                  padding: "3px 9px",
                  borderRadius: 999,
                  fontSize: 10,
                }}
              >
                {categoryLabel(cat)}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                <PictX name="pin" size={12} stroke={sub} strokeWidth={1.8} />
                {hub.state}
                {hub.city ? `・${hub.city}` : ""}
              </span>
            </div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 800,
                margin: "0 0 6px",
                letterSpacing: "0.01em",
              }}
            >
              {hub.name}
            </h1>
            {coupon.description && (
              <div
                style={{
                  fontSize: 13,
                  color: "#555",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                }}
              >
                {coupon.description}
              </div>
            )}
          </div>
        )}

        {/* How to use */}
        <div style={{ padding: "24px 20px 0" }}>
          <SectionHeader icon="ticket" title="クーポンの使い方" />
          <div
            style={{
              marginTop: 12,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Step
              n={1}
              title="お会計前にお店に提示"
              body="スタッフに「Boomin'のクーポン」と伝えてください"
            />
            <Step
              n={2}
              title="ボタンを押して使用"
              body="その場で「クーポンを使う」をタップ。タイマーが30秒間表示されます"
            />
            <Step
              n={3}
              title="特典を受け取って完了"
              body="店員さんに画面のアニメーションを確認してもらいましょう"
            />
          </div>
        </div>

        {/* Conditions */}
        {coupon && (
          <div style={{ padding: "24px 20px 0" }}>
            <SectionHeader icon="bell" title="ご利用条件" />
            <div
              style={{
                marginTop: 12,
                background: "#fff",
                border: "1px solid #F0E6DC",
                borderRadius: 16,
                overflow: "hidden",
              }}
            >
              {(
                [
                  ["有効期限", expiry || "—"],
                  ["利用回数", "お一人様1回まで"],
                ] as [string, string][]
              ).map(([k, v], i) => (
                <div
                  key={k}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    borderTop: i === 0 ? 0 : "1px solid #F4ECE2",
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: "#777" }}>{k}</span>
                  <span style={{ fontWeight: 600, color: "#1A1A1A", textAlign: "right" }}>
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Merchant info */}
        {hub && (
          <div style={{ padding: "24px 20px 0" }}>
            <SectionHeader icon="shop" title="店舗情報" />
            <a
              href={`/h/${hub.id}`}
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "#fff",
                border: "1px solid #F0E6DC",
                borderRadius: 16,
                padding: 14,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 14,
                  overflow: "hidden",
                  background: brandSoft,
                  border: "1px solid #F0E6DC",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {hub.image ? (
                  <img
                    src={hub.image}
                    alt={hub.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <PictX name="shop" size={32} stroke={brand} strokeWidth={1.4} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    lineHeight: 1.35,
                    marginBottom: 4,
                  }}
                >
                  {hub.name}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    fontSize: 12,
                    color: "#777",
                    lineHeight: 1.6,
                  }}
                >
                  <span style={{ flexShrink: 0, marginTop: 2 }}>
                    <PictX name="pin" size={12} stroke="#777" strokeWidth={1.8} />
                  </span>
                  {fullAddress(hub)}
                </div>
              </div>
              <span style={{ flexShrink: 0 }}>
                <PictX name="chevron" size={18} stroke="#bbb" strokeWidth={1.8} />
              </span>
            </a>
          </div>
        )}

        {/* Map */}
        {hub && hub.latitude && hub.longitude && (
          <div style={{ padding: "24px 20px 0" }}>
            <SectionHeader icon="map" title="お店の場所" />
            <div
              style={{
                marginTop: 12,
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid #F0E6DC",
                background: "#fff",
              }}
            >
              <ShopMap lat={hub.latitude} lng={hub.longitude} name={hub.name} />
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 12,
                  color: "#444",
                  lineHeight: 1.7,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span>{fullAddress(hub)}</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${hub.latitude},${hub.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: brandSoft,
                      color: brand,
                      border: 0,
                      fontWeight: 700,
                      fontSize: 11,
                      padding: "7px 12px",
                      borderRadius: 999,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      textDecoration: "none",
                    }}
                  >
                    地図で見る →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <AppFooter />

        {/* Sticky CTA */}
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "14px 20px calc(env(safe-area-inset-bottom, 0px) + 16px)",
            background: "linear-gradient(to top, #FFFBF7 70%, rgba(255,251,247,0))",
            maxWidth: 640,
            margin: "0 auto",
            zIndex: 20,
          }}
        >
          <button
            onClick={onUse}
            disabled={loading || !available}
            style={{
              width: "100%",
              height: 56,
              borderRadius: 999,
              border: 0,
              background: !available ? "#D6D0C6" : brand,
              color: "#fff",
              fontSize: 16,
              fontWeight: 800,
              cursor: loading || !available ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: !available
                ? "none"
                : `0 8px 20px ${brand}55, 0 1px 0 rgba(255,255,255,0.4) inset`,
              fontFamily: "inherit",
            }}
          >
            <PictX name="ticket" size={20} stroke="#fff" strokeWidth={1.8} />
            {available ? "クーポンを使う" : "使用済みです"}
          </button>
        </div>
      </div>
    </>
  );
};

const shareBtn: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 999,
  border: "1px solid #F0E6DC",
  cursor: "pointer",
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

type Crumb = { name: string; href?: string };

const buildBreadcrumb = ({
  state,
  city,
  addressLine1,
  cat,
  catLabel,
  hubName,
}: {
  state: string;
  city: string;
  addressLine1: string;
  cat: RedesignCategory;
  catLabel: string;
  hubName: string;
}): {
  visible: Crumb[];
  jsonLd: object;
} => {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://boomin.jp";
  const visible: Crumb[] = [{ name: "ホーム", href: "/" }];
  if (state) visible.push({ name: state, href: `/area/${state}` });
  if (city) visible.push({ name: city, href: `/area/${city}` });
  if (addressLine1) visible.push({ name: addressLine1, href: `/area/${addressLine1}` });
  if (catLabel) {
    const catArea = addressLine1 || city || state;
    visible.push({
      name: catLabel,
      href: catArea ? `/area/${catArea}/${cat}` : undefined,
    });
  }
  if (hubName) visible.push({ name: hubName });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: visible.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      ...(c.href ? { item: `${origin}${c.href}` } : {}),
    })),
  };
  return { visible, jsonLd };
};

const Breadcrumb: React.FC<{ crumbs: Crumb[] }> = ({ crumbs }) => (
  <nav
    aria-label="パンくずリスト"
    style={{
      padding: "10px 16px 6px",
      fontSize: 11,
      color: "#777",
      overflowX: "auto",
      whiteSpace: "nowrap",
      WebkitOverflowScrolling: "touch",
    }}
  >
    {crumbs.map((c, i) => {
      const last = i === crumbs.length - 1;
      return (
        <React.Fragment key={`${c.name}-${i}`}>
          {c.href && !last ? (
            <a
              href={c.href}
              style={{
                color: "#777",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              {c.name}
            </a>
          ) : (
            <span
              style={{
                color: last ? "#1A1A1A" : "#777",
                fontWeight: last ? 700 : 600,
              }}
              aria-current={last ? "page" : undefined}
            >
              {c.name}
            </span>
          )}
          {!last && (
            <span style={{ margin: "0 6px", color: "#bbb" }}>›</span>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

const Ticket: React.FC<{
  coupon: Coupon;
  hub: Hub | null;
  expiry: string;
  tag: string;
}> = ({ coupon, expiry, tag }) => (
  <div
    style={{
      position: "relative",
      borderRadius: 22,
      overflow: "hidden",
      background: "#fff",
      filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.12))",
    }}
  >
    <div
      style={{
        background: `linear-gradient(135deg, ${brand} 0%, ${accent} 100%)`,
        padding: "16px 18px 18px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 360 90"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, opacity: 0.18 }}
      >
        <circle cx="320" cy="20" r="40" fill="#fff" />
        <circle cx="280" cy="70" r="22" fill="#fff" />
      </svg>
      <div style={{ position: "relative" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "rgba(255,255,255,0.25)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "4px 9px",
            borderRadius: 999,
            backdropFilter: "blur(4px)",
          }}
        >
          <PictX name="flame" size={12} stroke="#fff" strokeWidth={2} />
          {tag}
        </div>
        <div
          style={{
            color: "#fff",
            fontSize: 22,
            fontWeight: 800,
            lineHeight: 1.25,
            marginTop: 8,
            letterSpacing: "0.01em",
            wordBreak: "break-word",
          }}
        >
          {coupon.title}
        </div>
      </div>
    </div>
    <div style={{ position: "relative", height: 16 }}>
      <div
        style={{
          position: "absolute",
          left: -8,
          top: 0,
          bottom: 0,
          width: 16,
          background: "#FFFBF7",
          borderRadius: "50%",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: -8,
          top: 0,
          bottom: 0,
          width: 16,
          background: "#FFFBF7",
          borderRadius: "50%",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 16,
          right: 16,
          top: "50%",
          borderTop: "1.5px dashed #E5DBD0",
        }}
      />
    </div>
    <div style={{ padding: "8px 18px 18px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: "#999", fontWeight: 600 }}>有効期限</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A1A" }}>
            {expiry || "—"}
          </div>
        </div>
        <div style={{ width: 1, height: 28, background: "#F0E6DC" }} />
        <div>
          <div style={{ fontSize: 10, color: "#999", fontWeight: 600 }}>利用回数</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A1A" }}>
            1回 / おひとり
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TicketSkeleton: React.FC = () => (
  <div
    style={{
      borderRadius: 22,
      height: 200,
      background: "#fff",
      border: "1px solid #F0E6DC",
    }}
  />
);

const SectionHeader: React.FC<{ icon: PictName; title: string }> = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: `${brand}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PictX name={icon} size={16} stroke={brand} strokeWidth={1.8} />
    </div>
    <h2
      style={{
        fontSize: 15,
        fontWeight: 800,
        margin: 0,
        letterSpacing: "0.02em",
      }}
    >
      {title}
    </h2>
  </div>
);

const Step: React.FC<{ n: number; title: string; body: string }> = ({ n, title, body }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      background: "#fff",
      borderRadius: 14,
      padding: 14,
      border: "1px solid #F0E6DC",
    }}
  >
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: 999,
        background: brand,
        color: "#fff",
        fontSize: 12,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {n}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11, color: "#777", lineHeight: 1.6 }}>{body}</div>
    </div>
  </div>
);

const ShopMap: React.FC<{ lat: number; lng: number; name: string }> = ({ lat, lng, name }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  if (!GOOGLE_MAPS_API_KEY || !isLoaded) {
    return <div style={{ height: 160, background: "#F2EDE5" }} />;
  }
  return (
    <div style={{ height: 160 }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat, lng }}
        zoom={16}
        options={{
          disableDefaultUI: true,
          gestureHandling: "none",
          clickableIcons: false,
        }}
      >
        <Marker position={{ lat, lng }} title={name} />
      </GoogleMap>
    </div>
  );
};

export default DetailScreen;
